import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import logo from './logo.png';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ valid: false, errors: [] });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  const navigate = useNavigate();

  // Check if recovery token exists in URL
  useEffect(() => {
    const checkToken = async () => {
      try {
        // Check URL hash for token
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const type = hashParams.get('type');

        if (accessToken && type === 'recovery') {
          setHasToken(true);
        } else {
          setStatus('Invalid or missing password reset link. Please request a new one.');
          setHasToken(false);
        }
      } catch (error) {
        console.error('Token check error:', error);
        setStatus('Error validating reset link. Please request a new one.');
        setHasToken(false);
      }
    };

    checkToken();
  }, []);

  // Real-time password validation
  useEffect(() => {
    if (password.length > 0) {
      validatePassword(password);
    } else {
      setPasswordStrength({ valid: false, errors: [] });
    }
  }, [password]);

  const validatePassword = async (pwd) => {
    const errors = [];

    if (pwd.length < 8) {
      errors.push('At least 8 characters');
    }
    if (!/[a-zA-Z]/.test(pwd)) {
      errors.push('At least one letter');
    }
    if (!/[0-9]/.test(pwd)) {
      errors.push('At least one number');
    }

    setPasswordStrength({
      valid: errors.length === 0,
      errors
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');

    // Validation
    if (!hasToken) {
      setStatus('Invalid reset link. Please request a new one.');
      return;
    }

    if (!password || !confirm) {
      setStatus('Please enter and confirm your new password.');
      return;
    }

    if (password !== confirm) {
      setStatus('Passwords do not match.');
      return;
    }

    if (!passwordStrength.valid) {
      setStatus('Password does not meet strength requirements.');
      return;
    }

    setLoading(true);

    try {
      // Update password via Supabase Auth
      const { data, error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error('Password update error:', error);

        // Better error messages
        if (error.message.includes('session')) {
          setStatus('Your reset link has expired or is invalid. Please request a new one.');
        } else if (error.message.includes('token')) {
          setStatus('Invalid reset token. Please request a new password reset link.');
        } else if (error.message.includes('same')) {
          setStatus('New password must be different from your old password.');
        } else {
          setStatus('Failed to reset password. Please try requesting a new reset link.');
        }
      } else {
        setStatus('Password reset successful! Redirecting to sign in...');
        setTimeout(() => navigate('/signin'), 2000);
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setStatus('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (password.length === 0) return '#e0eaff';
    if (passwordStrength.valid) return '#10b981';
    if (passwordStrength.errors.length <= 1) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f7f9fb 0%, #e0eaef 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: 450, margin: '0 auto', padding: '32px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <img src={logo} alt="Taealam Logo" style={{ width: 90, height: 90, objectFit: 'contain', marginBottom: 18, borderRadius: 18, boxShadow: '0 2px 12px #e0eaff' }} />

        <form onSubmit={handleSubmit} style={{ background: '#fff', padding: '32px 28px 28px 28px', borderRadius: 20, boxShadow: '0 4px 24px #e0eaff', width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column' }}>
          <h2 style={{ marginBottom: 8, textAlign: 'center', color: '#232B3E', fontWeight: 800, fontSize: 26, letterSpacing: 0.5 }}>Reset Your Password</h2>
          <p style={{ marginBottom: 24, textAlign: 'center', color: '#64748b', fontSize: 14 }}>Enter your new password below</p>

          {/* New Password */}
          <label style={{ alignSelf: 'flex-start', color: '#232B3E', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>New Password</label>
          <div style={{ position: 'relative', width: '100%', marginBottom: 8 }}>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 40px 12px 12px',
                borderRadius: 10,
                border: `1.5px solid ${getPasswordStrengthColor()}`,
                fontSize: 16,
                background: '#f7f9fb',
                outline: 'none',
                transition: 'border 0.2s',
                boxSizing: 'border-box'
              }}
              placeholder="Enter new password"
              disabled={!hasToken}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: 20
              }}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {/* Password strength indicator */}
          {password.length > 0 && (
            <div style={{ marginBottom: 16, fontSize: 13 }}>
              <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                <div style={{ flex: 1, height: 4, background: passwordStrength.errors.length < 3 ? getPasswordStrengthColor() : '#e0eaff', borderRadius: 2 }} />
                <div style={{ flex: 1, height: 4, background: passwordStrength.errors.length < 2 ? getPasswordStrengthColor() : '#e0eaff', borderRadius: 2 }} />
                <div style={{ flex: 1, height: 4, background: passwordStrength.valid ? getPasswordStrengthColor() : '#e0eaff', borderRadius: 2 }} />
              </div>
              {passwordStrength.valid ? (
                <span style={{ color: '#10b981', fontWeight: 600 }}>✓ Strong password</span>
              ) : (
                <ul style={{ margin: 0, paddingLeft: 20, color: '#64748b' }}>
                  {passwordStrength.errors.map((err, idx) => (
                    <li key={idx} style={{ marginBottom: 2 }}>{err}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Confirm Password */}
          <label style={{ alignSelf: 'flex-start', color: '#232B3E', fontWeight: 600, marginBottom: 6, fontSize: 14 }}>Confirm New Password</label>
          <div style={{ position: 'relative', width: '100%', marginBottom: 22 }}>
            <input
              type={showConfirm ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 40px 12px 12px',
                borderRadius: 10,
                border: `1.5px solid ${confirm && password !== confirm ? '#ef4444' : '#e0eaff'}`,
                fontSize: 16,
                background: '#f7f9fb',
                outline: 'none',
                transition: 'border 0.2s',
                boxSizing: 'border-box'
              }}
              placeholder="Confirm new password"
              disabled={!hasToken}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              style={{
                position: 'absolute',
                right: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#64748b',
                fontSize: 20
              }}
            >
              {showConfirm ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>

          {confirm && password !== confirm && (
            <div style={{ color: '#ef4444', fontSize: 13, marginTop: -16, marginBottom: 16 }}>
              Passwords do not match
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            style={{
              width: '100%',
              padding: 14,
              borderRadius: 10,
              background: hasToken && passwordStrength.valid && password === confirm && password ? '#FF7426' : '#e0eaff',
              color: hasToken && passwordStrength.valid && password === confirm && password ? '#fff' : '#94a3b8',
              fontWeight: 700,
              fontSize: 18,
              border: 'none',
              cursor: hasToken && passwordStrength.valid && password === confirm && password ? 'pointer' : 'not-allowed',
              boxShadow: hasToken && passwordStrength.valid ? '0 2px 8px #ffe5d1' : 'none',
              marginBottom: 8,
              transition: 'all 0.2s'
            }}
            disabled={loading || !hasToken || !passwordStrength.valid || password !== confirm || !password}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>

          {/* Status Message */}
          {status && (
            <div style={{
              color: status.includes('successful') ? '#10b981' : status.includes('expired') || status.includes('Invalid') ? '#ef4444' : '#f59e0b',
              marginTop: 14,
              textAlign: 'center',
              fontWeight: 600,
              fontSize: 14,
              padding: 12,
              background: status.includes('successful') ? '#f0fdf4' : status.includes('expired') || status.includes('Invalid') ? '#fef2f2' : '#fffbeb',
              borderRadius: 8
            }}>
              {status}
            </div>
          )}

          {/* Back to Sign In */}
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => navigate('/signin')}
              style={{
                background: 'none',
                border: 'none',
                color: '#2563eb',
                cursor: 'pointer',
                fontSize: 14,
                textDecoration: 'underline',
                padding: 0
              }}
            >
              Back to Sign In
            </button>
          </div>
        </form>
      </div>
    </div>
  );
} 